import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Download, RefreshCw, 
  Eye, Lock, Unlock, Wallet as WalletIcon,
  User, Bitcoin, Shield,
  AlertCircle, ChevronLeft, ChevronRight,
  BarChart3, User as UserIcon, Copy, MoreVertical,
  Calendar, Activity, TrendingUp,
  DollarSign, Zap, CreditCard,
  Users, Coins, TrendingDown
} from 'lucide-react';
import { AdminWalletAPI, AdminAPIUtils } from '../services/api';

function WalletsPage() {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    minBalance: '',
    maxBalance: ''
  });
  const [actionMenu, setActionMenu] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchWallets();
  }, [pagination.page, filters]);

  const fetchStats = async () => {
    try {
      const data = await AdminWalletAPI.getWalletStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch wallet stats:', err);
    }
  };

  const fetchWallets = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (filters.type !== 'all') {
        params.type = filters.type;
      }

      if (filters.status !== 'all') {
        params.status = filters.status;
      }

      if (filters.minBalance) {
        params.min_balance = filters.minBalance;
      }

      if (filters.maxBalance) {
        params.max_balance = filters.maxBalance;
      }

      const response = await AdminWalletAPI.getWallets(params);
      setWallets(response.wallets || []);
      setPagination({
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 20,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.pages || 1
      });
    } catch (err) {
      setError(AdminAPIUtils.handleError(err, false).message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchWallets();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      type: 'all',
      status: 'all',
      minBalance: '',
      maxBalance: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const exportWallets = async () => {
    try {
      await AdminWalletAPI.exportWallets(filters);
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const toggleWalletLock = async (walletId, currentlyLocked) => {
    const action = currentlyLocked ? 'unlock' : 'lock';
    const reason = prompt(`Enter reason to ${action} this wallet:`);
    
    if (!reason) return;
    
    try {
      await AdminWalletAPI.toggleWalletLock(walletId, !currentlyLocked, reason);
      fetchWallets(); // Refresh the list
      alert(`Wallet ${action}ed successfully`);
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const getWalletTypeBadge = (type) => {
    const config = {
      bitcoin: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20' },
      fiat: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
      bonus: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
      game: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' }
    };
    
    const configItem = config[type] || { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' };
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} border ${configItem.border} flex items-center gap-1.5`}>
        {type === 'bitcoin' && <Bitcoin className="w-3 h-3" />}
        {type === 'fiat' && <DollarSign className="w-3 h-3" />}
        {type === 'bonus' && <Zap className="w-3 h-3" />}
        {type === 'game' && <Coins className="w-3 h-3" />}
        {type?.charAt(0).toUpperCase() + type?.slice(1)}
      </span>
    );
  };

  const getWalletStatusBadge = (locked) => {
    if (locked) {
      return (
        <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/10 text-rose-400 border border-rose-500/20 flex items-center gap-1.5">
          <Lock className="w-3 h-3" />
          Locked
        </span>
      );
    }
    return (
      <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
        <Unlock className="w-3 h-3" />
        Active
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    // You could replace this with a toast notification
    alert('Copied to clipboard!');
  };

  const statCards = [
    {
      title: 'Total Balance',
      value: AdminAPIUtils.formatBTC(stats?.total_balance || 0) + ' BTC',
      icon: <WalletIcon className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      change: '+5.2%',
      changeText: 'from last week',
      trend: 'up'
    },
    {
      title: 'Total Locked',
      value: AdminAPIUtils.formatBTC(stats?.total_locked || 0) + ' BTC',
      icon: <Lock className="w-5 h-5" />,
      color: 'from-rose-500 to-red-500',
      change: '12 wallets',
      changeText: 'currently locked',
      trend: 'neutral'
    },
    {
      title: 'Total Wallets',
      value: pagination.total || 0,
      icon: <Users className="w-5 h-5" />,
      color: 'from-blue-500 to-cyan-500',
      change: '48',
      changeText: 'new this month',
      trend: 'up'
    },
    {
      title: 'Avg Balance',
      value: AdminAPIUtils.formatBTC((parseFloat(stats?.total_balance || 0)) / Math.max(pagination.total, 1)) + ' BTC',
      icon: <BarChart3 className="w-5 h-5" />,
      color: 'from-violet-500 to-purple-500',
      change: '2.1 BTC',
      changeText: 'per user avg',
      trend: 'up'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
            Wallet Management
          </h1>
          <p className="text-charcoal-gray mt-1">Manage user wallets and monitor cryptocurrency balances</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={exportWallets}
            className="px-4 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={fetchWallets}
            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02]"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div 
            key={index} 
            className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:bg-night-blue/70 transition-all duration-300 hover:scale-[1.02] group"
          >
            <div className="flex items-start justify-between mb-6">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color} shadow-lg`}>
                {card.icon}
              </div>
              {card.trend === 'up' && (
                <div className="flex items-center text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">+{card.change}</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-2xl font-bold text-white">{card.value}</p>
              <p className="text-charcoal-gray text-sm uppercase tracking-wider">{card.title}</p>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-sm text-charcoal-gray">
                <span className={`font-medium ${card.trend === 'up' ? 'text-emerald-400' : card.trend === 'down' ? 'text-rose-400' : 'text-gray-400'}`}>
                  {card.trend === 'up' ? '+' : ''}{card.change}
                </span>{' '}
                {card.changeText}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters Card */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by username, email, or wallet address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all duration-300"
                  />
                </div>
              </div>
              <button
                type="submit"
                className="px-5 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02]"
              >
                Search
              </button>
            </form>
          </div>

          {/* Type Filter */}
          <div>
            <div className="flex items-center gap-3">
              <Filter className="text-charcoal-gray w-5 h-5" />
              <select
                value={filters.type}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, type: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
              >
                <option value="all">All Types</option>
                <option value="bitcoin">Bitcoin</option>
                <option value="fiat">Fiat</option>
                <option value="bonus">Bonus</option>
                <option value="game">Game</option>
              </select>
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <div className="flex items-center gap-3">
              <Shield className="text-charcoal-gray w-5 h-5" />
              <select
                value={filters.status}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, status: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="locked">Locked</option>
              </select>
            </div>
          </div>
        </div>

        {/* Balance Range Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div>
            <label className="block text-sm font-medium text-charcoal-gray mb-2">Min Balance (BTC)</label>
            <div className="relative">
              <Bitcoin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-4 h-4" />
              <input
                type="number"
                step="0.00000001"
                placeholder="0.00000000"
                value={filters.minBalance}
                onChange={(e) => setFilters(prev => ({ ...prev, minBalance: e.target.value }))}
                className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-charcoal-gray mb-2">Max Balance (BTC)</label>
            <div className="relative">
              <Bitcoin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-4 h-4" />
              <input
                type="number"
                step="0.00000001"
                placeholder="100.00000000"
                value={filters.maxBalance}
                onChange={(e) => setFilters(prev => ({ ...prev, maxBalance: e.target.value }))}
                className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Filter Actions */}
        <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-800">
          <div className="text-sm text-charcoal-gray">
            Showing <span className="text-white font-medium">{wallets.length}</span> of{' '}
            <span className="text-white font-medium">{pagination.total}</span> wallets
          </div>
          <button
            onClick={clearFilters}
            className="text-sm text-charcoal-gray hover:text-white transition-colors hover:bg-gray-800/50 px-3 py-1.5 rounded-lg"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Wallets Table */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
              <p className="text-charcoal-gray">Loading wallets...</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-rose-400 mx-auto mb-4" />
            <div className="text-rose-300 mb-4 font-medium">Error: {error}</div>
            <button
              onClick={fetchWallets}
              className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300"
            >
              Retry
            </button>
          </div>
        ) : wallets.length === 0 ? (
          <div className="p-8 text-center">
            <WalletIcon className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No wallets found</h3>
            <p className="text-charcoal-gray mb-6">Try adjusting your search or filters</p>
            <button
              onClick={clearFilters}
              className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-800">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <WalletIcon className="w-4 h-4" />
                        Wallet
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <UserIcon className="w-4 h-4" />
                        User
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Balance
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Type
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Status
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Created
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {wallets.map((wallet) => (
                    <tr 
                      key={wallet.id} 
                      className="hover:bg-gray-800/30 transition-colors group"
                    >
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-white group-hover:text-sky-300 transition-colors">
                            {wallet.wallet_type === 'bitcoin' ? 'Bitcoin Wallet' : 'Fiat Wallet'}
                          </div>
                          <div className="text-xs text-charcoal-gray">ID: {wallet.id}</div>
                          {wallet.bitcoin_address ? (
                            <div className="flex items-center gap-2 mt-2">
                              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800/50 rounded-lg">
                                <Bitcoin className="w-3 h-3 text-orange-400" />
                                <span className="text-xs text-gray-300 truncate max-w-[120px]">
                                  {wallet.bitcoin_address.substring(0, 16)}...
                                </span>
                                <button
                                  onClick={() => copyToClipboard(wallet.bitcoin_address)}
                                  className="text-gray-400 hover:text-white transition-colors"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-xs text-charcoal-gray italic mt-1">
                              No Bitcoin address
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                            {wallet.username?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-white">{wallet.username}</div>
                            <div className="text-xs text-charcoal-gray truncate max-w-[140px]">{wallet.email}</div>
                            <div className="text-xs text-gray-500">User ID: {wallet.user_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <div className="text-xl font-bold text-emerald-400">
                            {AdminAPIUtils.formatBTC(wallet.balance || 0)} BTC
                          </div>
                          {parseFloat(wallet.locked_balance || 0) > 0 && (
                            <div className="text-xs text-rose-400 bg-rose-500/10 px-2 py-1 rounded inline-block">
                              🔒 Locked: {AdminAPIUtils.formatBTC(wallet.locked_balance || 0)} BTC
                            </div>
                          )}
                          <div className="text-xs text-charcoal-gray">
                            Total Tx: {wallet.transaction_stats?.total_transactions || 0}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getWalletTypeBadge(wallet.wallet_type)}
                      </td>
                      <td className="px-6 py-4">
                        {getWalletStatusBadge(wallet.locked)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="text-sm text-white">{formatDate(wallet.created_at)}</div>
                          <div className="text-xs text-charcoal-gray">
                            {wallet.last_activity ? AdminAPIUtils.timeAgo(wallet.last_activity) : 'No activity'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => navigate(`/wallets/${wallet.id}`)}
                            className="p-2 bg-gray-800/50 hover:bg-sky-500/20 text-sky-400 hover:text-sky-300 rounded-lg transition-all duration-300 hover:scale-110"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={() => toggleWalletLock(wallet.id, wallet.locked)}
                            className={`p-2 rounded-lg transition-all duration-300 hover:scale-110 ${
                              wallet.locked
                                ? 'bg-gray-800/50 hover:bg-emerald-500/20 text-emerald-400 hover:text-emerald-300'
                                : 'bg-gray-800/50 hover:bg-rose-500/20 text-rose-400 hover:text-rose-300'
                            }`}
                            title={wallet.locked ? 'Unlock Wallet' : 'Lock Wallet'}
                          >
                            {wallet.locked ? (
                              <Unlock className="w-4 h-4" />
                            ) : (
                              <Lock className="w-4 h-4" />
                            )}
                          </button>
                          
                          <div className="relative">
                            <button
                              onClick={() => setActionMenu(actionMenu === wallet.id ? null : wallet.id)}
                              className="p-2 bg-gray-800/50 hover:bg-gray-700 text-gray-400 hover:text-white rounded-lg transition-all duration-300 hover:scale-110"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                            {actionMenu === wallet.id && (
                              <div className="absolute right-0 mt-2 w-48 bg-night-blue border border-gray-800 rounded-xl shadow-2xl py-2 z-50">
                                <div className="px-4 py-2 border-b border-gray-800">
                                  <p className="text-sm font-medium text-white">Wallet Actions</p>
                                </div>
                                <button
                                  onClick={() => {
                                    const amount = prompt('Enter amount in BTC to adjust (positive to add, negative to deduct):');
                                    if (amount !== null) {
                                      const description = prompt('Enter reason for adjustment:');
                                      if (description) {
                                        AdminWalletAPI.adjustBalance(wallet.id, amount, description)
                                          .then(() => {
                                            alert('Balance adjusted successfully');
                                            fetchWallets();
                                          })
                                          .catch(AdminAPIUtils.handleError);
                                      }
                                    }
                                    setActionMenu(null);
                                  }}
                                  className="w-full px-4 py-2.5 text-sm text-amber-400 hover:bg-amber-500/10 flex items-center gap-2 transition-colors"
                                >
                                  <Bitcoin className="w-4 h-4" />
                                  Adjust Balance
                                </button>
                                <button
                                  onClick={() => navigate(`/users/${wallet.user_id}`)}
                                  className="w-full px-4 py-2.5 text-sm text-blue-400 hover:bg-blue-500/10 flex items-center gap-2 transition-colors"
                                >
                                  <UserIcon className="w-4 h-4" />
                                  View User
                                </button>
                                <button
                                  onClick={() => navigate(`/users/${wallet.user_id}/transactions`)}
                                  className="w-full px-4 py-2.5 text-sm text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-2 transition-colors"
                                >
                                  <Activity className="w-4 h-4" />
                                  View Transactions
                                </button>
                                <div className="px-4 py-2 border-t border-gray-800">
                                  <p className="text-xs text-charcoal-gray">Wallet ID: {wallet.id}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-charcoal-gray">
                    Page <span className="font-medium text-white">{pagination.page}</span> of{' '}
                    <span className="font-medium text-white">{pagination.totalPages}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                        pagination.page === 1
                          ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <div className="flex items-center space-x-1">
                      {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                            className={`px-4 py-2.5 min-w-[40px] rounded-xl transition-all duration-300 ${
                              pagination.page === pageNum
                                ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg'
                                : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
                        <>
                          <span className="px-2 text-charcoal-gray">...</span>
                          <button
                            onClick={() => setPagination(prev => ({ ...prev, page: pagination.totalPages }))}
                            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl hover:scale-105 transition-all duration-300"
                          >
                            {pagination.totalPages}
                          </button>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
                      disabled={pagination.page === pagination.totalPages}
                      className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                        pagination.page === pagination.totalPages
                          ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                      }`}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default WalletsPage;